import styled, { css, keyframes } from "styled-components";

/*
 * Header, v2 redizajn ("editorial punk"):
 * --bg-app sa blur(12px), border-bottom 1px --border (bez narandžaste linije
 * i teških senki). Aktivan link: --brand + 2px underline.
 */

/* Fiksna visina (umesto padding-a), mora da se poklapa sa padding-top
   Content-a u MainContainer.js da između headera i sadržaja nema procepa. */
export const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: ${(props) => (props.$isAtTop ? "64px" : "54px")};
  display: flex;
  align-items: center;
  background: rgba(19, 16, 16, 0.82);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border);
  transition: height 0.3s ease;
  will-change: height;

  @media (max-width: 768px) {
    height: 56px;
  }
`;

/* Tanka brand linija na dnu headera, prati progres skrola stranice.
   Širina se menja direktno preko ref-a (transform: scaleX), bez re-rendera. */
export const ScrollProgress = styled.span`
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 2px;
  background: linear-gradient(90deg, var(--brand), #ffa06b);
  box-shadow: 0 0 8px rgba(255, 107, 53, 0.5);
  transform: scaleX(0);
  transform-origin: left;
  pointer-events: none;

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

export const NavContainer = styled.nav`
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.5rem;
`;

export const Logo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-family: var(--font-display);
  font-size: 1.5rem;
  letter-spacing: 0.02em;
  color: var(--text-primary);
  cursor: pointer;
  user-select: none;
  transition: color 0.2s ease;
  line-height: 1;

  &:hover {
    color: var(--brand);
  }
`;

/* ------------------------------------------------------------------ */
/* Logo, znak + МРЧ logotext: intro na prvi load, spin/talas na hover */
/* ------------------------------------------------------------------ */
const markIn = keyframes`
  0% { transform: rotate(-360deg) scale(0); opacity: 0; }
  55% { opacity: 1; }
  100% { transform: rotate(0deg) scale(1); opacity: 1; }
`;

const markSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const letterIn = keyframes`
  from { transform: translateY(0.75em) rotate(10deg); opacity: 0; }
  to { transform: translateY(0) rotate(0deg); opacity: 1; }
`;

const letterWave = keyframes`
  0% { transform: translateY(0) scale(1); }
  40% { transform: translateY(-4px) scale(1.12); }
  100% { transform: translateY(0) scale(1); }
`;

/* Intro na spoljnom elementu, hover-spin na unutrašnjem svg-u,
   razdvojeno da unhover ne restartuje intro animaciju */
export const LogoMark = styled.span`
  display: inline-flex;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  animation: ${markIn} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  transition: filter 0.25s ease;

  svg {
    width: 100%;
    height: 100%;
    display: block;
    will-change: transform;
  }

  /* Spin se pokreće JS klasom (na mouseenter) i UVEK se odvrti do kraja,
     ne živi u :hover pravilu, pa nema skoka kad se hover prekine na pola. */
  svg.spin {
    animation: ${markSpin} 0.7s cubic-bezier(0.4, 0, 0.2, 1);
  }

  ${Logo}:hover & {
    filter: drop-shadow(0 0 9px rgba(255, 107, 53, 0.65));
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    svg.spin {
      animation: none;
    }
  }
`;

export const LogoText = styled.span`
  display: inline-flex;
`;

/* Intro na slovu, talas na unutrašnjem spanu, isti razlog kao kod znaka */
export const LogoLetter = styled.span`
  display: inline-block;
  animation: ${letterIn} 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  animation-delay: calc(0.3s + var(--i) * 90ms);

  > span {
    display: inline-block;
  }

  ${Logo}:hover & > span {
    animation: ${letterWave} 0.45s cubic-bezier(0.2, 0.8, 0.2, 1);
    animation-delay: calc(var(--i) * 70ms);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    ${Logo}:hover & > span {
      animation: none;
    }
  }
`;

export const NavMenu = styled.ul`
  display: flex;
  align-items: center;
  gap: 2rem;
  list-style: none;
  margin: 0;
  padding: 0;

  @media (max-width: 900px) {
    display: none;
  }
`;

const navItemIn = keyframes`
  from { transform: translateY(-14px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const underlinePop = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

const activePop = keyframes`
  0% { transform: scale(1); }
  40% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

export const StyledLi = styled.li`
  cursor: pointer;
  animation: ${navItemIn} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  animation-delay: calc(0.45s + var(--i) * 80ms);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const StyledSpan = styled.span`
  position: relative;
  display: inline-block;
  padding: 0.35rem 0;
  font-size: 0.95rem;
  font-weight: ${(props) => (props.$active ? 600 : 500)};
  color: ${(props) =>
    props.$active ? "var(--brand)" : "var(--text-secondary)"};
  transition: color 0.2s ease;
  white-space: nowrap;

  /* Underline: raste sleva na hover, povlači se nadesno na izlazu */
  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 2px;
    height: 1.5px;
    background: var(--brand);
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.25s ease;
  }

  &:hover {
    color: ${(props) =>
      props.$active ? "var(--brand)" : "var(--text-primary)"};

    &::after {
      transform: scaleX(1);
      transform-origin: left;
    }
  }

  ${(props) =>
    props.$active &&
    css`
      animation: ${activePop} 0.35s ease;

      &::after {
        transform: scaleX(1);
        transform-origin: left;
        box-shadow: 0 0 8px rgba(255, 107, 53, 0.45);
        animation: ${underlinePop} 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
      }
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    &::after {
      animation: none;
    }
  }
`;

export const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

/* Ghost ikonica (search, korpa) */
export const IconButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: var(--radius-control);
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  transition:
    background 0.18s ease,
    color 0.18s ease;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    color: var(--brand);
  }
`;

const badgePop = keyframes`
  0% { transform: scale(1); }
  35% { transform: scale(1.65); }
  100% { transform: scale(1); }
`;

const countIn = keyframes`
  from { transform: translateY(80%); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

export const CartBadge = styled.span`
  position: absolute;
  top: 2px;
  right: 0;
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: var(--brand);
  color: #ffffff;
  border-radius: var(--radius-pill);
  font-size: 10.5px;
  font-weight: 700;
  line-height: 1;
  pointer-events: none;

  ${(p) =>
    p.$pop &&
    css`
      animation: ${badgePop} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1);
    `}
`;

/* Broj u badge-u, uklizava odozdo pri promeni (key={count}) */
export const CartCount = styled.span`
  display: block;
  animation: ${countIn} 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);
`;

/* ------------------------------------------------------------------ */
/* Fly-to-cart: kuglica leti do korpe (X linearno + Y ease-in = luk),  */
/* pa kapljice prsnu oko badge-a                                       */
/* ------------------------------------------------------------------ */
const flyX = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(var(--fly-dx)); }
`;

const flyY = keyframes`
  0% { transform: translateY(0) scale(1); opacity: 1; }
  85% { opacity: 1; }
  100% { transform: translateY(var(--fly-dy)) scale(0.3); opacity: 0.85; }
`;

export const FlyDotOuter = styled.span`
  position: fixed;
  z-index: 1100;
  pointer-events: none;
  will-change: transform;
  animation: ${flyX} 0.7s linear forwards;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 0.01s;
  }
`;

export const FlyDotInner = styled.span`
  display: block;
  width: 15px;
  height: 15px;
  margin: -7.5px 0 0 -7.5px;
  border-radius: 50%;
  background: var(--brand);
  box-shadow:
    0 0 10px rgba(255, 107, 53, 0.85),
    0 0 22px rgba(255, 107, 53, 0.45);
  will-change: transform;
  animation: ${flyY} 0.7s cubic-bezier(0.55, 0, 0.85, 0.4) forwards;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 0.01s;
  }
`;

const splash = keyframes`
  0% { transform: translate(0, 0) scale(1); opacity: 1; }
  100% { transform: translate(var(--sx), var(--sy)) scale(0.15); opacity: 0; }
`;

/* Sidro za kapljice, centar badge-a */
export const SplashWrap = styled.span`
  position: absolute;
  top: 10px;
  right: 8px;
  width: 0;
  height: 0;
  pointer-events: none;
`;

export const SplashDrop = styled.span`
  position: absolute;
  width: 5px;
  height: 5px;
  margin: -2.5px 0 0 -2.5px;
  border-radius: 50%;
  background: var(--brand);
  box-shadow: 0 0 6px rgba(255, 107, 53, 0.7);
  animation: ${splash} 0.55s ease-out forwards;
`;

export const MobileMenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: var(--radius-control);
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }

  @media (max-width: 900px) {
    display: inline-flex;
  }
`;

/* ------------------------------------------------------------------ */
/* Mobilni full-screen meni, Archivo Black stavke, velike             */
/* ------------------------------------------------------------------ */
export const MobileMenuOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(19, 16, 16, 0.98);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transition:
    opacity 0.25s ease,
    visibility 0.25s ease;
`;

export const MobileMenuContent = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0 1.5rem 2rem;
  transform: translateY(${(props) => (props.$isOpen ? "0" : "-12px")});
  transition: transform 0.25s ease;
`;

export const MobileMenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid var(--border);
`;

export const MobileMenuLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-family: var(--font-display);
  font-size: 1.5rem;
  color: var(--text-primary);
  cursor: pointer;

  svg {
    width: 28px;
    height: 28px;
    display: block;
  }
`;

export const MobileMenuClose = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: var(--radius-control);
  background: transparent;
  color: var(--text-primary);
  font-size: 1.5rem;
  cursor: pointer;

  &:hover {
    color: var(--brand);
  }
`;

export const MobileMenuNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 2rem;
`;

export const MobileNavLink = styled.div`
  font-family: var(--font-display);
  font-size: clamp(1.8rem, 8vw, 2.6rem);
  text-transform: uppercase;
  letter-spacing: -0.01em;
  line-height: 1.25;
  padding: 0.4rem 0;
  color: ${(props) => (props.$active ? "var(--brand)" : "var(--text-primary)")};
  cursor: pointer;
  transition:
    color 0.18s ease,
    transform 0.18s ease;

  &:hover {
    color: var(--brand);
    transform: translateX(6px);
  }
`;
